import React, { Fragment } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';
import { graphql } from 'react-relay';
import { type GraphQLTaggedNode } from 'relay-runtime';

import AiSparkleIcon from '@atlaskit/icon/core/ai-sparkle';
import Lozenge from '@atlaskit/lozenge/lozenge';
import { LinkMenuItem } from '@atlaskit/side-nav-items/link-menu-item';

import {
	ShortcutsSection,
	type ShortcutsSectionFragmentKey,
} from '@confluence/space-shortcuts/entry-points/shortcutsSection.ts';
import {
	SpaceViewsController,
	type SpaceViewsPersistenceFragmentKey,
	type SpaceViewsRootFragmentKey,
} from '@confluence/space-views';
import {
	BlogTree,
	type BlogTreeInitialDataFragmentKey,
} from '@confluence/blog-tree/entry-points/BlogTree.tsx';
import type { PageTreeInitialDataFragmentKey } from '@confluence/page-tree/entry-points/PageTreeLoader.tsx';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { TransparentErrorBoundary } from '@confluence/error-boundary/entry-points/TransparentErrorBoundary.ts';
import { expVal, expValEquals } from '@confluence/feature-experiments';
import { LoadableAfterPaint } from '@confluence/loadable';
import { SPACE_SUGGESTIONS } from '@confluence/named-routes/entry-points/routes.ts';
import { useIsOnRoute } from '@confluence/route-manager/entry-points/RouteState.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import type { SpaceOperationsFragment$key } from '@confluence/entity-operations/entry-points/SpaceOperationsFragmentKey.ts';
import { hasSpaceOperation, MANAGE_CONTENT } from '@confluence/space-utils/entry-points/graphql.ts';

const AgentContextItem = LoadableAfterPaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-AgentContextItem" */ '@confluence/admin-side-navigation/entry-points/AgentContextItem.ts'
			)
		).AgentContextItem,
});

const i18n = defineMessages({
	suggestionsHeader: {
		id: 'side-navigation.space-navigation.suggestions.header',
		defaultMessage: 'Suggestions',
		description:
			'The text shown as a section heading for the Suggestions tab in the Confluence space sidebar.',
	},
	contentIntelligenceHeader: {
		id: 'side-navigation.space-navigation.content-intelligence.header',
		defaultMessage: 'Content intelligence',
		description:
			'The text shown as a link in the Confluence space sidebar to navigate to the space content intelligence page (previously labelled Suggestions).',
	},
	contentIntelligenceNewLozenge: {
		id: 'side-navigation.space-navigation.content-intelligence.new-lozenge',
		defaultMessage: 'New',
		description:
			'Label on a badge shown next to the Content intelligence link in the Confluence space sidebar, indicating the feature is newly released.',
	},
});

export const CommonSpaceNavigationContentSpaceFragment: GraphQLTaggedNode = graphql`
	fragment CommonSpaceNavigationContent_space on Space {
		id
		key
		homepage {
			id
		}
		operations {
			operation
			targetType
		}
		...SpaceOperationsFragment
		didContainUserContent
	}
`;

export const CommonSpaceNavigationContent = ({
	showShortcutsSection,
	space,
	spaceKey,
	quick,
	homepageId,
	loading,
	isSpaceAdmin,
	isBlogNavigation,
	blogsHidden,
	pageTreeFinishedLoading,
	onDragDropSuccess,
	onContentTreeLoadComplete,
	shortcutsFragmentRef,
	spaceViewsRootFragmentRef,
	spaceViewsPersistenceFragmentRef,
	treeInitialDataFragmentRef,
}: {
	showShortcutsSection: any;
	space: any;
	spaceKey: any;
	quick: any;
	homepageId: any;
	loading: any;
	isSpaceAdmin: any;
	isBlogNavigation: any;
	blogsHidden: any;
	pageTreeFinishedLoading: any;
	onDragDropSuccess: any;
	onContentTreeLoadComplete: any;
	shortcutsFragmentRef?: ShortcutsSectionFragmentKey;
	spaceViewsRootFragmentRef?: SpaceViewsRootFragmentKey;
	spaceViewsPersistenceFragmentRef?: SpaceViewsPersistenceFragmentKey;
	treeInitialDataFragmentRef?: PageTreeInitialDataFragmentKey & BlogTreeInitialDataFragmentKey;
}): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const { isAdminHubAIEnabled, isAIEarlyAccessEnabled } = useSessionData();
	// Derive from the SSR-preloaded SpaceNavigationQuery's `space.operations` (passed via the `space`
	// prop) instead of firing a separate client-only query, so the Suggestions link can render during
	// SSR and on first client render without a hydration mismatch.
	const canManageContent = hasSpaceOperation(space?.operations, MANAGE_CONTENT);
	const isSuggestionsSelected = useIsOnRoute(SPACE_SUGGESTIONS);

	return (
		<>
			{canManageContent &&
				isAIEarlyAccessEnabled &&
				expVal('confluence_space_organizer', 'isEnabled', false) && (
					<LinkMenuItem
						href={SPACE_SUGGESTIONS.toUrl({ spaceKey })}
						elemBefore={<AiSparkleIcon label="" color="currentColor" />}
						elemAfter={
							expVal('confluence_content_intelligence', 'isEnabled', false) ? (
								<Lozenge
									appearance="discovery"
									isBold
									testId="space-content-intelligence-new-lozenge"
								>
									{formatMessage(i18n.contentIntelligenceNewLozenge)}
								</Lozenge>
							) : undefined
						}
						data-testid="space-suggestions-link"
						isSelected={isSuggestionsSelected}
					>
						{formatMessage(
							expVal('confluence_content_intelligence', 'isEnabled', false)
								? i18n.contentIntelligenceHeader
								: i18n.suggestionsHeader,
						)}
					</LinkMenuItem>
				)}
			{showShortcutsSection &&
				(shortcutsFragmentRef ? (
					<ShortcutsSection
						isSpaceAdmin={isSpaceAdmin}
						spaceKey={spaceKey}
						spaceSidebarLinksFragmentRef={shortcutsFragmentRef}
						spaceId={space?.id || null}
					/>
				) : (
					<ShortcutsSection
						isSpaceAdmin={isSpaceAdmin}
						spaceKey={spaceKey}
						links={quick}
						spaceId={space?.id || null}
					/>
				))}

			{!loading ? (
				<div data-vc="pageTree" data-testid="pageTree">
					<SpaceViewsController
						spaceViewsRootFragmentRef={spaceViewsRootFragmentRef}
						spaceViewsPersistenceFragmentRef={spaceViewsPersistenceFragmentRef}
						pageTreeInitialDataFragmentRef={treeInitialDataFragmentRef}
						key={spaceKey} // Remount on space change to reset Space views state
						homepageId={homepageId}
						onDragDropSuccess={onDragDropSuccess}
						isPeekingFromBlogs={isBlogNavigation}
						onContentTreeLoadComplete={onContentTreeLoadComplete}
						spaceHasUserContent={space?.didContainUserContent}
						pageTreeFinishedLoading={pageTreeFinishedLoading}
					/>
				</div>
			) : (
				<Fragment />
			)}

			{isAdminHubAIEnabled &&
			isAIEarlyAccessEnabled &&
			expValEquals('cc_space_agents_md_context', 'isEnabled', true) ? (
				<TransparentErrorBoundary attribution={Attribution.MIGRATIONS_TOOLING}>
					<AgentContextItem spaceKey={spaceKey} />
				</TransparentErrorBoundary>
			) : null}

			{!loading && !blogsHidden && (
				<BlogTree
					initialDataFragmentRef={treeInitialDataFragmentRef}
					spaceKey={spaceKey}
					pageTreeFinishedLoading={pageTreeFinishedLoading}
					spaceOperationsRef={
						expVal('confluence_contextual_create_relay_space_ops', 'isEnabled', false)
							? (space as SpaceOperationsFragment$key)
							: undefined
					}
				/>
			)}
		</>
	);
};
